import arrowUpIcon from "@assets/images/arrow-up-white.svg";
import folderIcon from "@assets/images/folder-open-new.svg";
import infoIcon from "@assets/images/info.svg";
import warningIcon from "@assets/images/warning-red.svg";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import DialogProcessing from "@saleor/components/DialogProcessing/DialogProcessing";
import MyTypography from "@saleor/components/MyTypography";
import { useUploadCustomerMaterialsMutation } from "@saleor/graphql";
import useNotifier from "@saleor/hooks/useNotifier";
import { FormikProps } from "formik";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from "react";
import { FileError, useDropzone } from "react-dropzone";
import InlineSVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import FileLine from "src/alternatedMaterial/components/UploadFile/FileLine";

import DialogUploadFail from "../DialogUploadFail";
import useStyle from "./styles";

interface IFile {
  type: string;
  size: number;
  name: string;
  path: string;
}

interface MaterialUploadFile {
  onUploadSuccess?: () => void;
  formik: FormikProps<any>;
  setRecords: Dispatch<SetStateAction<any>>;
}

export default ({
  onUploadSuccess,
  formik,
  setRecords
}: MaterialUploadFile) => {
  const classes = useStyle();
  const intl = useIntl();

  const {
    getRootProps,
    getInputProps,
    acceptedFiles,
    fileRejections
  } = useDropzone({
    multiple: false,
    accept: ".xlsx",
    maxSize: 10000000
  });

  const notify = useNotifier();
  const [file, setFile] = useState<IFile>();
  const [openDialogProcessing, setOpenDialogProcessing] = useState(false);
  const [openUploadFail, setOpenUploadFail] = useState(false);
  const [error, setError] = useState(false);
  const [errorList, setErrorList] = useState([]);

  const [uploadFile, uploadState] = useUploadCustomerMaterialsMutation({
    onCompleted: data => {
      setOpenDialogProcessing(false);
      if (data?.uploadCustomerMaterials?.status) {
        setRecords(data?.uploadCustomerMaterials?.rows);
        setFile(null);
        setError(false);
        onUploadSuccess();
      } else {
        setOpenUploadFail(true);
        setErrorList(data?.uploadCustomerMaterials?.errors);
      }
    },
    onError: errors => {
      setOpenDialogProcessing(false);
      notify({
        status: "error",
        apiMessage: errors.message
      });
    }
  });

  const onFileReady = () => {
    let result: any;
    if (acceptedFiles.length > 0) {
      result = acceptedFiles[0];
    } else if (fileRejections.length > 0) {
      result = fileRejections[0].file;
    }
    setFile(result);
  };

  useEffect(() => {
    onFileReady();
  }, [acceptedFiles, fileRejections]);

  const errorMessage = useMemo<Pick<FileError, "code"> | string>(() => {
    if (!!fileRejections[0]?.errors && file) {
      return fileRejections[0]?.errors[0].code;
    }
    return "";
  }, [fileRejections, file]);

  const canUpload = !errorMessage && !!file;

  const onUpload = () => {
    if (canUpload) {
      setOpenDialogProcessing(true);
      uploadFile({
        variables: {
          file,
          input: {
            soldToCode: formik?.values?.soldTo,
            saleOrgCode: formik?.values?.saleOrganization,
            distributionChannel: formik?.values?.distributionChannel
          }
        }
      });
    }
  };

  const onRemove = () => {
    setFile(null);
    setError(false);
  };

  const body = (
    <Box display={"flex"} flexDirection="column" flex={1}>
      <section className={classes.uploadBox}>
        <Box
          {...getRootProps({ className: "dropzone" })}
          flexDirection="column"
          alignItems={"center"}
          height="93%"
          justifyContent="center"
          display={"flex"}
        >
          <input {...getInputProps()} />
          <InlineSVG src={folderIcon} />
          <Typography className={classes.dragTitle}>
            Drag & Drop to Upload File, or
          </Typography>
          <Typography component={"u"} className={`${classes.browseFile} `}>
            Click here to browse
          </Typography>
        </Box>
      </section>
      <FileLine path={file?.path} onRemove={onRemove} />
    </Box>
  );

  const infoMessage1 = intl.formatMessage({
    defaultMessage: "รองรับไฟล์นามสกุล",
    description: "รองรับไฟล์นามสกุล"
  });

  const infoMessage3 = intl.formatMessage({
    defaultMessage: "เท่านั้น",
    description: "เท่านั้น"
  });
  const infoMessage = () => (
    <>
      {infoMessage1} <strong>.xlsx</strong> {infoMessage3}
    </>
  );
  const uploadedMessage1 = intl.formatMessage({
    defaultMessage: "กรุณากด",
    description: "uploadedMessage1"
  });
  const uploadedMessage3 = intl.formatMessage({
    defaultMessage: "เพื่อยืนยัน",
    description: "uploadedMessage3"
  });

  const uploadedMessage = () => (
    <>
      {uploadedMessage1} <strong>Upload File</strong> {uploadedMessage3}
    </>
  );

  const getErrorMessage = () => {
    const invalidMessage1 = intl.formatMessage({
      defaultMessage: "รองรับไฟล์นามสกุล",
      description: "invalidMessage1"
    });
    const invalidMessage2 = intl.formatMessage({
      defaultMessage: "เท่านั้น, กรุณาลองใหม่",
      description: "invalidMessage2"
    });

    const invalidMessage = () => (
      <>
        {invalidMessage1} <strong>.xlsx</strong> {invalidMessage2}
      </>
    );
    if (errorMessage === "file-too-large") {
      return intl.formatMessage({
        defaultMessage: "ไฟล์มีขนาดเกิน 10 MB, กรุณาลองใหม่",
        description: "fileTooLarge"
      });
    }
    if (errorMessage === "file-invalid-type") {
      return invalidMessage();
    }
    return fileRejections[0]?.errors[0]?.message;
  };

  const isShowWarningIcon = errorMessage || error;

  const errorLineMessage = intl.formatMessage({
    defaultMessage: "อัปโหลดไฟล์ไม่สำเร็จ",
    description: "errorLineMessage"
  });

  const explainMessage = intl.formatMessage({
    defaultMessage: "ดูข้อมูลเพิ่มเติม",
    description: "explainMessage"
  });
  return (
    <Box
      className={classes.container}
      display={"flex"}
      flexDirection={"column"}
    >
      <MyTypography variant="subtitle1" className={classes.uploadBoxTitle}>
        <span className={classes.uploadBoxTitleBold}>Upload :</span> Customer
        Material
      </MyTypography>
      {body}
      <Box display={"flex"} justifyContent={"space-between"} marginTop={"10px"}>
        <Box display={"flex"} justifyContent={"space-between"}>
          <Box alignItems="center" display="flex" justifyContent={"center"}>
            <InlineSVG
              src={isShowWarningIcon ? warningIcon : infoIcon}
              style={{ marginTop: 0 }}
              className={classes.messageIcon}
            />
            {error && (
              <React.Fragment>
                <Typography className={classes.note}>
                  {errorLineMessage} &nbsp;
                </Typography>
                <Typography
                  onClick={() => setOpenUploadFail(true)}
                  component={"u"}
                  style={{ fontWeight: 700 }}
                  className={classes.notes}
                >
                  {explainMessage}
                </Typography>
              </React.Fragment>
            )}
            {!error && (
              <Typography
                className={classes.note}
                style={{
                  color: errorMessage ? "#E41B17" : "#555555"
                }}
              >
                {errorMessage
                  ? getErrorMessage()
                  : !canUpload
                  ? infoMessage()
                  : ""}
                {canUpload ? uploadedMessage() : " "}
              </Typography>
            )}
          </Box>
        </Box>
        <Button
          className={classes.button}
          onClick={onUpload}
          style={{
            cursor: canUpload ? "pointer" : "not-allowed",
            backgroundColor: canUpload ? "#009FB8" : "#E0E0E0",
            height: "40px"
          }}
        >
          {uploadState.loading ? (
            <CircularProgress size={16} />
          ) : (
            <InlineSVG src={arrowUpIcon} />
          )}

          <Typography className={classes.uploadButtonTitle}>
            {intl.formatMessage({
              defaultMessage: "Upload File"
            })}
          </Typography>
        </Button>
      </Box>
      <DialogProcessing
        open={openDialogProcessing}
        onClose={() => setOpenDialogProcessing(false)}
        title="ระบบกำลังอัปโหลดไฟล์"
        description="กรุณารอสักครู่ / โปรดอย่ากด Refresh หรือปิดหน้าจอ"
      />
      <DialogUploadFail
        onConfirm={() => {
          setOpenUploadFail(false);
          setError(true);
        }}
        open={openUploadFail}
        data={errorList}
      />
    </Box>
  );
};
